import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';

import { Maybe, SingleOrArray } from '@plugins/shared/types/helpers';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

import type { AllEntityTypesWithFails } from '../../private/utils/types.private';
import { generateIdArray } from '../../private/utils/utils';
export type PickedEntitiesValues = AllEntityTypesWithFails | undefined;
export type PickedEntities = Map<string, PickedEntitiesValues>;

/** Management of picked entities through adding, removing, clearing,
 * setting, and keeping track of ids that are to be excluded from search.
 * It is not provided at module level to allow for component specific states
 * to be managed.
 *
 * If parts of the component need separate entities management,
 * the `PickedEntitiesDirective` or `PickedEntitiesComponent` can be used. */
@Injectable()
export class PickedEntitiesService {
  private pickedEntitiesSubject = new BehaviorSubject<PickedEntities>(
    new Map(),
  );
  private excludeFromSearchIds: string[] = [];
  pickedEntities$ = this.pickedEntitiesSubject.asObservable();
  pickedIds$ = this.pickedEntitiesSubject
    .asObservable()
    .pipe(map((pickedMap) => [...pickedMap.keys()]));

  /** Retrieve current id keys. */
  get pickedIds(): string[] {
    return [...this.pickedEntitiesSubject.getValue().keys()];
  }

  /** Retrieve ids that will be excluded from search */
  get excludedIds(): string[] {
    return this.excludeFromSearchIds;
  }

  /** Current state of picked entities map. */
  get pickedEntities(): PickedEntities {
    return this.pickedEntitiesSubject.getValue();
  }

  get entities(): AllEntityTypesWithFails[] {
    return [...this.pickedEntities.values()].filter(
      (val): val is AllEntityTypesWithFails => val !== undefined,
    );
  }

  /** Retrieve target entity by its id, if it exists. */
  getEntity(id: string): PickedEntitiesValues {
    return this.pickedEntities.get(id);
  }

  /** Re-set the list with provided entity ids.
   *
   * If `preserveEntities` is `true` (default) operation preserves
   * any pre-existing entities that match passed ids. If `false`, it
   * replaces them with `undefined`*/
  setIds(entityIds: SingleOrArray<string>, preserveEntities = true): void {
    const current = preserveEntities ? this.pickedEntities : undefined;
    this.pickedEntitiesSubject.next(
      new Map<string, PickedEntitiesValues>(
        generateIdArray(entityIds).map((key) => [key, current?.get(key)]),
      ),
    );
  }

  /** Add new ids to entity map if they do not yet exist. */
  addIds(entityIds: SingleOrArray<string>): void {
    const entitiesMap = this.pickedEntities;
    generateIdArray(entityIds).forEach((id) => {
      if (!entitiesMap.has(id)) {
        entitiesMap.set(id, undefined);
      }
    });
    this.pickedEntitiesSubject.next(entitiesMap);
  }

  /** Remove ids from entity map, along with their values */
  removeIds(entityIds: SingleOrArray<string>): void {
    const arr = generateIdArray(entityIds);
    if (!arr.length) return;
    const entitiesMap = this.pickedEntities;
    arr.forEach((key) => entitiesMap.delete(key));
    this.pickedEntitiesSubject.next(entitiesMap);
  }

  /** Clear all entity map entries */
  removeAllIds(): void {
    this.pickedEntitiesSubject.next(new Map());
  }

  /** Re-set list of ids that should be excluded from search*/
  excludeIdsFromSearch(excludedIds: Maybe<SingleOrArray<string>>): void {
    this.excludeFromSearchIds = [...generateIdArray(excludedIds)];
  }

  /** Upsert entity values.*/
  setEntityValue(
    entity: SingleOrArray<AllEntityTypesWithFails>,
  ): PickedEntities {
    const entityMap = this.pickedEntities;
    returnAsArray(entity).forEach((entity) => entityMap.set(entity.id, entity));
    this.pickedEntitiesSubject.next(entityMap);
    return entityMap;
  }
}
